import { useEffect } from 'react';

import { DispositiveProvisionSuggestionsProvider } from '@/modules/aiDP/context/DispositiveProvisionSuggestions.provider';
import { EntitySuggestionsProvider } from '@/modules/aiSuggestions/context/EntitySuggestions.provider';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { EntityDetailsAwareRoute } from '@/modules/entities/contexts/entityDetails/EntityDetailsAwareRoute';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenModal';

interface EditEntityModalMountProps {
  /** The entity to edit — from the URL-driven trowser state. */
  entityId: string;
  /** The section to open at, if any. */
  section?: EditEntitySection;
  onClose: () => void;
  /** Called after a successful save, so the host can refetch on save not close. */
  onAfterSave: () => void;
}

/**
 * Mounts the edit-entity modal for a single targeted entity from a presentation
 * surface (entities list or estate-waterfall diagram). Replicates the provider
 * stack the entity summary panel uses around this modal (entity details plus the
 * entity/DP suggestion contexts the modal reads), keyed to the requested entity
 * id. Rendered only while the URL-driven trowser is open, so switching entities
 * is just a remount against the new id. The trowser's open/close/section live in
 * the URL (see `useEditEntityTrowser`), so it deep-links and closes on Back.
 */
export function EditEntityModalMount({
  entityId,
  section,
  onClose,
  onAfterSave,
}: EditEntityModalMountProps) {
  return (
    <EntityDetailsAwareRoute entityId={entityId}>
      <EntitySuggestionsProvider>
        <DispositiveProvisionSuggestionsProvider targetId={entityId}>
          <EditEntityModalGate
            entityId={entityId}
            section={section}
            onClose={onClose}
            onAfterSave={onAfterSave}
          />
        </DispositiveProvisionSuggestionsProvider>
      </EntitySuggestionsProvider>
    </EntityDetailsAwareRoute>
  );
}

function EditEntityModalGate({
  entityId: requestedEntityId,
  section,
  onClose,
  onAfterSave,
}: EditEntityModalMountProps) {
  const { entityId, entityNotFound, entityType } = useEntityDetailsContext();

  // If `EntityDetailsAwareRoute`'s query can't resolve the entity (not found,
  // load error, empty result), the context never matches our id — tear the
  // flow down instead of hanging with no modal and no way to recover.
  useEffect(() => {
    if (entityNotFound) {
      onClose();
    }
  }, [entityNotFound, onClose]);

  // `EntityDetailsAwareRoute` populates the entity-details context from a query.
  // Wait until it resolves to our requested entity (and its type) before
  // opening — the entity type isn't carried in the URL, so it's derived here.
  if (entityId !== requestedEntityId || !entityType) {
    return null;
  }

  return (
    <EditEntitySplitScreenModal
      entityType={entityType}
      initialSection={section ?? EditEntitySection.BASIC_INFORMATION}
      isOpen
      onClose={onClose}
      onAfterSave={onAfterSave}
    />
  );
}
